import React from 'react';
import { BringgFontIcons } from '@bringg/bringg-icons';
import { getDateFormat } from '../../components/date-display/date-display';
import Tabs from '../../components/tabs/tabs';
import TaskNotesAttachments from '../../components/task-notes-attachments/task-notes-attachments';
import { filter as _filter, sumBy as _sumBy } from 'lodash';
import TaskInventories from '../../components/task-inventories/task-inventories';
import TaskTrackingLinks from '../../components/task-tracking-links/task-tracking-links';
import {
	Inventory,
	SharedLocation,
	TaskInventory,
	TaskNote,
	WayPoint,
	CustomUnitsDefinition,
	Scan,
	OrderPageTabs,
	NoteType
} from '@bringg/types';
import { MapData } from '../../components/mapbox/mapbox';
import { MarkerData } from 'types/maps.consts';
import { OrderPageConfiguration, Translations, WindowCommunicationService } from './waypoint-card.consts';
import { TaskNotes } from 'components';

import './waypoint-card-tabs.scss';

interface Props {
	translations: Translations;
	textualTaskNotes: TaskNote[];
	onNoteAdded?: (note: string) => void;
	hourFormat: string;
	mapData?: MapData;
	tabPaneTitle: (title: string, icon) => React.ReactNode;
	wayPointData: WayPoint;
	editable: boolean;
	canAddNotes: boolean;
	timezoneForDisplay?: string;
	graphicalTaskNotes: TaskNote[];
	scans?: Scan[];
	taskInventories?: TaskInventory[];
	onQuantityChange?: (massQuantityUpdate: TaskInventory[]) => void;
	onInventoriesChange?: (inventories: Inventory[]) => void;
	editInventoryQuantity: boolean;
	editDimension: boolean;
	sharedLocations?: SharedLocation[];
	timeZone?: string;
	configuration: OrderPageConfiguration;
	setMapMarker: (marker: MarkerData) => void;
	allInventoriesInTask?: TaskInventory[];
	customUnitsDefinition?: CustomUnitsDefinition;
	taskInventoryScanMap?: Map<number, Scan>;
	doNotMultipleWeightByQuantity?: boolean;
	shouldShowLinkSourceTask?: boolean;
	defaultTab?: OrderPageTabs;
	WindowCommunicationService: WindowCommunicationService;
}

const WaypointCardTabs: React.FC<Props> = ({
	translations,
	textualTaskNotes,
	onNoteAdded,
	hourFormat,
	mapData,
	tabPaneTitle,
	wayPointData,
	editable,
	canAddNotes,
	timezoneForDisplay,
	graphicalTaskNotes,
	scans,
	taskInventories,
	onQuantityChange,
	onInventoriesChange,
	editInventoryQuantity,
	editDimension,
	sharedLocations,
	timeZone,
	configuration,
	setMapMarker,
	allInventoriesInTask,
	customUnitsDefinition,
	taskInventoryScanMap,
	doNotMultipleWeightByQuantity,
	shouldShowLinkSourceTask,
	defaultTab,
	WindowCommunicationService
}) => {
	const taskNotesToDisplay = graphicalTaskNotes.filter(taskNote => {
		if (taskNote.type !== NoteType.Form) return true;
		return taskNote.note && Object.keys(taskNote.note).length;
	});

	const getInventorySum = (): number => {
		return _sumBy(_filter(taskInventories, { parent_task_inventory_id: null }), obj => obj.original_quantity);
	};

	const totalAttachments = () => {
		let totalSum = taskNotesToDisplay.length;
		totalSum += scans?.length || 0;
		return totalSum;
	};

	const notesKey = `waypoint-${wayPointData.id}-notes`;
	const attachmentsKey = `waypoint-${wayPointData.id}-attachments`;
	const inventoriesKey = `waypoint-${wayPointData.id}-inventories`;
	const trackingKey = `waypoint-${wayPointData.id}-tracking-links`;

	const defaultTabKey = () => {
		switch (defaultTab) {
			case OrderPageTabs.notes:
				return notesKey;
			case OrderPageTabs.attachments:
				return attachmentsKey;
			case OrderPageTabs.inventories:
				return inventoriesKey;
			case OrderPageTabs.tracking: {
				if (sharedLocations) {
					return trackingKey;
				} else {
					return undefined;
				}
			}
			default:
				return undefined;
		}
	};

	const tabItems = [
		{
			label: tabPaneTitle(` ${translations.notes} (${textualTaskNotes.length})`, BringgFontIcons.NoteLight),
			key: notesKey,
			children: (
				<TaskNotes
					taskNotes={textualTaskNotes}
					onNoteAdded={onNoteAdded}
					dateFormat={getDateFormat(hourFormat)}
					mapData={mapData}
					translations={translations}
					editable={editable}
					canAddNotes={canAddNotes}
					timezone={timezoneForDisplay}
					setMapMarker={setMapMarker}
				/>
			)
		},
		{
			label: tabPaneTitle(` ${translations.attachments} (${totalAttachments()})`, BringgFontIcons.Attachment),
			key: attachmentsKey,
			children: (
				<TaskNotesAttachments
					taskNotesAttachments={taskNotesToDisplay}
					scans={scans}
					dateFormat={getDateFormat(hourFormat)}
					mapData={mapData}
					translations={translations}
					timezone={timezoneForDisplay}
					setMapMarker={setMapMarker}
				/>
			)
		},
		{
			label: tabPaneTitle(` ${translations.inventories} (${getInventorySum()})`, BringgFontIcons.Inventory),
			key: inventoriesKey,
			children: (
				<TaskInventories
					taskInventories={taskInventories}
					onQuantityChange={onQuantityChange}
					onInventoriesChange={onInventoriesChange}
					translations={translations}
					editable={editable}
					flagsAccess={{
						enable_dispatcher_edit_task_inventory_quantity: editInventoryQuantity,
						enable_dimension_editing_in_ui: editDimension
					}}
					wayPointData={wayPointData}
					allInventoriesInTask={allInventoriesInTask}
					customUnitsDefinition={customUnitsDefinition}
					taskInventoryScanMap={taskInventoryScanMap}
					doNotMultipleWeightByQuantity={doNotMultipleWeightByQuantity}
					shouldShowLinkSourceTask={shouldShowLinkSourceTask}
				/>
			)
		}
	];

	if (sharedLocations) {
		tabItems.push({
			label: tabPaneTitle(` ${translations.trackingLinks} (${sharedLocations.length})`, BringgFontIcons.Location),
			key: trackingKey,
			children: (
				<TaskTrackingLinks
					WindowCommunicationService={WindowCommunicationService}
					sharedLocations={sharedLocations}
					timeZone={timeZone}
					dateFormat={getDateFormat(hourFormat)}
					translations={translations}
					useTeamTimezone={configuration.flags.useTeamTimezone}
				/>
			)
		});
	}

	return (
		<Tabs
			defaultActiveKey={defaultTabKey()}
			className="waypoint-card-tabs"
			data-test-id={`default-tab: ${defaultTabKey()}`}
			items={tabItems}
		/>
	);
};
export default WaypointCardTabs;
